import { useUnit } from 'effector-react';
import { generateUuid } from '@lib/uuid';
import { useNavigateMenuItem } from '@nx-frontend/shared/hooks';
import { useChanges } from '@nx-frontend/shared/hooks/useChangeEvents';
import { $virtualNodePayload, VirtualNodePayload, generatePageFx } from '@nx-frontend/shared/store/virtual-portal-node';
import { MenuItem, VirtualPortalPage } from '@nx-frontend/shared/types';
import { ChangeEventType } from '@nx-frontend/shared/types';
import { generateNoteGlobalId } from '@nx-frontend/shared/utils';

interface PageCreatePayload {
  openAfterCreate: boolean;
  title?: string;
  skipCreateNoteId?: boolean; // TODO: Delete after SIDEBAR release
}

export const usePageCreate = (): ((
  payload: PageCreatePayload,
  pagePayloadParam?: VirtualNodePayload
) => Promise<{ menuItem: MenuItem; node: VirtualPortalPage }>) => {
  const [pagePayloadStore, generatePage] = useUnit([$virtualNodePayload, generatePageFx]);
  const setChanges = useChanges();
  const navigateMenuItem = useNavigateMenuItem();

  return async (payload: PageCreatePayload, pagePayloadParam) => {
    const pagePayload = pagePayloadParam ?? pagePayloadStore;
    if (!pagePayload) {
      throw new Error('Missing node payload');
    }

    const { node, menuItem } = await generatePage({
      menuType: pagePayload.menuType,
      parentId: pagePayload.parentId,
      title: payload.title,
      visible: pagePayload.visible,
    });

    const batchId = generateUuid();
    setChanges({
      type: ChangeEventType.ADD_NODE,
      batchId,
      oldValue: undefined,
      newValue: node,
    });

    if (!payload?.skipCreateNoteId) {
      const noteId = generateNoteGlobalId();
      menuItem.targetId = noteId;
    }

    setChanges({
      type: ChangeEventType.ADD_MENU_ITEM,
      batchId,
      oldValue: undefined,
      newValue: { menuType: pagePayload.menuType, item: menuItem },
    });

    if (payload.openAfterCreate) {
      navigateMenuItem({ path: menuItem.url, originalPath: menuItem.originalUrl });
    }

    return {
      menuItem,
      node,
    };
  };
};
